import { cn } from '@/lib/cn';
import { useInView } from '@/lib/useInView';
import { getCachedVideoFrameUrl, getOrExtractVideoPreviewUrl } from '@/lib/videoFrame';
import { PlayCircle, Video } from 'lucide-react';
import { useEffect, useState } from 'react';

type VideoPreviewThumbProps = {
  videoUrl: string;
  thumbnailUrl?: string | null;
  alt?: string;
  className?: string;
  showPlayIcon?: boolean;
  imgClassName?: string;
  /** Só extrai frame do vídeo quando o tile entra na viewport. */
  lazy?: boolean;
};

/**
 * Mostra miniatura do vídeo: usa thumbnail do servidor ou extrai um frame no browser.
 */
export function VideoPreviewThumb({
  videoUrl,
  thumbnailUrl,
  alt = 'Miniatura do vídeo',
  className,
  showPlayIcon = true,
  imgClassName,
  lazy = false,
}: VideoPreviewThumbProps) {
  const [containerRef, inView] = useInView('280px');
  const shouldLoad = !lazy || inView || !!thumbnailUrl;

  const [frameUrl, setFrameUrl] = useState<string | null>(() =>
    thumbnailUrl || (!lazy ? getCachedVideoFrameUrl(videoUrl) : null) || null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!shouldLoad) return;

    if (thumbnailUrl) {
      setFrameUrl(thumbnailUrl);
      setFailed(false);
      return;
    }

    const cached = getCachedVideoFrameUrl(videoUrl);
    if (cached) {
      setFrameUrl(cached);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setFailed(false);

    void getOrExtractVideoPreviewUrl(videoUrl)
      .then((url) => {
        if (!cancelled) setFrameUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [videoUrl, thumbnailUrl, shouldLoad]);

  if (frameUrl) {
    return (
      <div ref={containerRef} className={cn('relative h-full w-full', className)}>
        <img
          src={frameUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          className={cn('h-full w-full object-cover', imgClassName)}
        />
        {showPlayIcon && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <PlayCircle className="h-8 w-8 text-white drop-shadow" />
          </div>
        )}
      </div>
    );
  }

  if (failed) {
    return (
      <div
        ref={containerRef}
        className={cn(
          'relative flex h-full w-full flex-col items-center justify-center gap-1 bg-manejai-ink/10 text-manejai-bark/45',
          className,
        )}
      >
        <Video className="h-7 w-7" />
        <span className="text-[10px] font-medium uppercase tracking-wide">Vídeo</span>
        {showPlayIcon && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/25">
            <PlayCircle className="h-8 w-8 text-white drop-shadow" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex h-full w-full items-center justify-center bg-manejai-ink/10',
        className,
      )}
    >
      <div className="h-8 w-8 animate-pulse rounded-full bg-manejai-sand/70" />
      {showPlayIcon && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/15">
          <PlayCircle className="h-8 w-8 text-white/80 drop-shadow" />
        </div>
      )}
    </div>
  );
}
